import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

/**
 * Google reCAPTCHA v2 checkbox widget, restoring what the legacy
 * user_login.html page rendered via Flask-WTF's RecaptchaField (LoginForm in
 * backend/app/forms.py) - dropped during the React migration on the
 * mistaken belief that recaptcha was never actually configured (see the
 * now-corrected comment in backend/internal/adapters/http/management/
 * auth.py's api_signup docstring). backend/app/__init__.py sets
 * RECAPTCHA_PUBLIC_KEY from this same site key.
 *
 * Site keys are meant to be embedded in client-side HTML by design - only
 * the secret key (backend-only) is sensitive.
 */
const RECAPTCHA_SITE_KEY = '6LcX8GsrAAAAAL1C83xGUFIZJsveLd2il0WWNYVv'

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: HTMLElement,
        params: {
          sitekey: string
          size?: 'normal' | 'compact'
          callback: (token: string) => void
          'expired-callback'?: () => void
        },
      ) => number
      reset: (widgetId?: number) => void
    }
    __onRecaptchaApiLoad?: () => void
  }
}

let scriptLoadPromise: Promise<void> | null = null

function loadRecaptchaScript(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise
  scriptLoadPromise = new Promise((resolve) => {
    window.__onRecaptchaApiLoad = () => resolve()
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?onload=__onRecaptchaApiLoad&render=explicit'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

export interface RecaptchaHandle {
  /** Clears the current token (single-use) - call after a failed login. */
  reset: () => void
}

export const Recaptcha = forwardRef<RecaptchaHandle, { onChange: (token: string | null) => void }>(
  function Recaptcha({ onChange }, ref) {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<number | null>(null)
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    useEffect(() => {
      let cancelled = false
      loadRecaptchaScript().then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return
        widgetIdRef.current = window.grecaptcha!.render(containerRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          // The standard widget is a fixed 304px iframe, wider than the form
          // on the narrowest phones; Google's compact variant fits there.
          size: containerRef.current.offsetWidth < 304 ? 'compact' : 'normal',
          callback: (token) => onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current(null),
        })
      })
      return () => {
        cancelled = true
      }
    }, [])

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current !== null) window.grecaptcha?.reset(widgetIdRef.current)
        onChangeRef.current(null)
      },
    }))

    return <div ref={containerRef} />
  },
)
